import { useState, useMemo } from 'react';
import {
  Box,
  HStack,
  Text,
  Spinner,
  Image,
  FormatNumber,
  createListCollection,
  type ComboboxRootProps,
} from '@chakra-ui/react';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '@/hooks/useDebounce';
import { useShopifyIntegrationQuery } from '@/hooks/queries/use-integrations-query';
import {
  useShopifyProductsQuery,
  type ShopifyProduct,
} from '@/hooks/queries/use-shopify-products-query';
import {
  ComboboxRoot,
  ComboboxControl,
  ComboboxInput,
  ComboboxContent,
  ComboboxItem,
  ComboboxTrigger,
  ComboboxIndicatorGroup,
} from '@/components/ui/combobox';
import { FlowShopifyNotConnected } from './FlowShopifyNotConnected';
import { ProductUnlistedBadge } from './ProductCard';
import { getProductDisplayPriceAmount } from './product-price-utils';

interface ProductSelectorProps {
  onToggle: (product: ShopifyProduct) => void;
  selectedProductIds: string[];
  maxProducts?: number;
  // Single-select mode (e.g. boost destination): picking a product replaces the
  // current selection and closes the menu. Defaults to the multi-select
  // behaviour used by the gifting collection picker.
  singleSelect?: boolean;
  portalled?: boolean;
  positioning?: ComboboxRootProps['positioning'];
}

interface ProductItem {
  value: string;
  label: string;
  product: ShopifyProduct;
}

export function ProductSelector({
  onToggle,
  selectedProductIds,
  maxProducts = 15,
  singleSelect = false,
  portalled = true,
  positioning,
}: ProductSelectorProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const { data: shopifyIntegration, isLoading: isIntegrationLoading } =
    useShopifyIntegrationQuery();
  // `data === undefined` means either "still loading" or "no Shopify integration",
  // so distinguish with `isLoading` to avoid flashing the empty state.
  const shopifyConnected = shopifyIntegration?.isConnected ?? false;

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useShopifyProductsQuery({
    search: debouncedSearch,
    enabled: shopifyConnected,
  });

  // The backend already writes a user-friendly sentence for this failure (e.g.
  // the Shopify token expired even though the integration record still says
  // "connected") — surface that instead of a generic fallback, and never the
  // raw error code.
  const fetchErrorMessage = isAxiosError(error) ? error.response?.data?.message : undefined;

  const products = useMemo(() => data?.pages.flatMap((page) => page?.products ?? []) ?? [], [data]);

  const isMaxReached = selectedProductIds.length >= maxProducts;

  const productItems: ProductItem[] = useMemo(
    () => products.map((p) => ({ value: p.id, label: p.title, product: p })),
    [products]
  );

  const collection = useMemo(
    () =>
      createListCollection({
        items: productItems,
        isItemDisabled: (item) => !selectedProductIds.includes(item.value) && isMaxReached,
      }),
    [productItems, selectedProductIds, isMaxReached]
  );

  const handleValueChange = (details: { value: string[] }) => {
    const currentSet = new Set(selectedProductIds);
    const newSet = new Set(details.value);

    // Find added item
    for (const id of details.value) {
      if (!currentSet.has(id)) {
        const product = products.find((p) => p.id === id);
        if (product) onToggle(product);
        return;
      }
    }

    // Find removed item
    for (const id of selectedProductIds) {
      if (!newSet.has(id)) {
        const product = products.find((p) => p.id === id);
        if (product) onToggle(product);
        return;
      }
    }
  };

  // Once the integration status has resolved and no Shopify store is connected,
  // the products query cannot run — show an explicit empty state instead of a
  // combobox that would sit in a silent retry loop.
  if (!isIntegrationLoading && !shopifyConnected) {
    return <FlowShopifyNotConnected />;
  }

  return (
    <Box w="full">
      <ComboboxRoot
        positioning={positioning}
        collection={collection}
        multiple={!singleSelect}
        closeOnSelect={singleSelect}
        selectionBehavior={singleSelect ? 'replace' : 'preserve'}
        value={selectedProductIds}
        onValueChange={handleValueChange}
        onInputValueChange={(details) => setSearch(details.inputValue)}
        openOnClick
        size="sm"
      >
        <ComboboxControl>
          <ComboboxInput
            placeholder={t('flows.properties.gift.searchProducts', 'Search products...')}
          />
          <ComboboxIndicatorGroup>
            <ComboboxTrigger />
          </ComboboxIndicatorGroup>
        </ComboboxControl>

        <ComboboxContent portalled={portalled}>
          {isLoading ? (
            <HStack p={3} justify="center">
              <Spinner size="xs" />
              <Text fontSize="sm" color="fg.muted">
                {t('flows.properties.gift.loadingProducts', 'Loading products...')}
              </Text>
            </HStack>
          ) : isError ? (
            <Box p={3} textAlign="center">
              <Text fontSize="sm" color="fg.muted">
                {fetchErrorMessage ??
                  t('flows.properties.gift.loadError', "Couldn't load products. Try again.")}
              </Text>
              <Text
                fontSize="sm"
                color="blue.500"
                mt={1}
                cursor="pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  refetch();
                }}
              >
                {t('common.retry', 'Retry')}
              </Text>
            </Box>
          ) : collection.items.length === 0 ? (
            <Box p={3} textAlign="center">
              <Text fontSize="sm" color="fg.muted">
                {t('flows.properties.gift.noProducts', 'No products found')}
              </Text>
            </Box>
          ) : (
            <>
              {collection.items.map((item) => {
                const priceAmount = getProductDisplayPriceAmount(item.product);

                return (
                  <ComboboxItem key={item.value} item={item}>
                    <HStack gap={2} flex={1}>
                      {item.product.featuredImageUrl ? (
                        <Image
                          src={item.product.featuredImageUrl}
                          alt={item.product.title}
                          boxSize="32px"
                          objectFit="cover"
                          borderRadius="sm"
                          flexShrink={0}
                        />
                      ) : (
                        <Box boxSize="32px" bg="bg.subtle" borderRadius="sm" flexShrink={0} />
                      )}
                      <Box flex={1} minW={0}>
                        <HStack gap={1.5}>
                          <Text fontWeight="medium" fontSize="sm" minW={0} truncate>
                            {item.product.title}
                          </Text>
                          <ProductUnlistedBadge status={item.product.status} />
                        </HStack>
                        <HStack gap={1} fontSize="xs" color="fg.muted">
                          <Text>
                            {priceAmount !== null && (
                              <FormatNumber
                                value={priceAmount}
                                style="currency"
                                currency={item.product.priceRange.min.currencyCode}
                              />
                            )}
                          </Text>
                          <Text>·</Text>
                          <Text>
                            {t('flows.properties.gift.inStock', '{{count}} in stock', {
                              count: item.product.totalInventory,
                            })}
                          </Text>
                        </HStack>
                      </Box>
                    </HStack>
                  </ComboboxItem>
                );
              })}

              {hasNextPage && (
                <Box
                  p={2}
                  textAlign="center"
                  cursor="pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    fetchNextPage();
                  }}
                  _hover={{ bg: 'bg.muted' }}
                >
                  {isFetchingNextPage ? (
                    <Spinner size="xs" />
                  ) : (
                    <Text fontSize="xs" color="blue.500">
                      {t('common.loadMore', 'Load more')}
                    </Text>
                  )}
                </Box>
              )}
            </>
          )}
        </ComboboxContent>
      </ComboboxRoot>

      {isMaxReached && (
        <Text fontSize="xs" color="fg.muted" mt={1}>
          {t('flows.properties.gift.maxProductsReached', 'Maximum of {{max}} products reached', {
            max: maxProducts,
          })}
        </Text>
      )}
    </Box>
  );
}
